import { useState } from 'react';

import { useDidMountEffect } from '../hooks';

export type ChangeCallback<T> = (newValue: T) => void;
export type Unsubscribe = () => void;

/**
 * Contains a value and allows to subscribe to changes of that value.
 */
export class Cell<T> {
    private innerValue: T;
    private subscribers: ReadonlyArray<(value: T) => void> = [];

    /**
     * Retrieves value stored in cell.
     */
    public get value(): T {
        return this.innerValue;
    }

    /**
     * Updates value stored in cell.
     */
    public set value(newValue: T) {
        this.innerValue = newValue;

        for (const subscriber of this.subscribers) {
            subscriber(this.innerValue);
        }
    }

    constructor(initialValue: T) {
        this.innerValue = initialValue;
    }

    public subscribe(onChange: ChangeCallback<T>): Unsubscribe {
        this.subscribers = [...this.subscribers, onChange];

        return () => {
            this.subscribers = this.subscribers.filter((subscriber) => subscriber !== onChange);
        };
    }
}

export type CellValue<C> = C extends Cell<infer V> ? V : never;

export function whenCellIs<T>(cell: Cell<T>, accept: (value: T) => boolean, signal?: AbortSignal): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        let unsubscribe = () => {};

        const unsubscribeCell = cell.subscribe((value) => {
            if (accept(value)) {
                resolve(value);
                unsubscribe();
            }
        });

        const abort = () => {
            unsubscribeCell();
            reject(new Error('AbortError'));
        };

        unsubscribe = () => {
            unsubscribeCell();
            signal?.removeEventListener?.('abort', abort);
        };

        if (signal) {
            if (signal.aborted) {
                unsubscribe();
            }

            signal.addEventListener('abort', abort, { once: true });
        }
    });
}

/**
 * Provide current value in cell to component.
 *
 * @param cell Cell to use
 * @returns Value in the cell
 */
export function useCellValue<T>(cell: Cell<T>): T {
    const [value, updateValue] = useState(cell.value);

    useDidMountEffect(() => cell.subscribe(updateValue));

    return value;
}
